import {
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	VelocityClient,
	MarketType,
	PerpMarketConfig,
	SpotMarketConfig,
	User,
} from '@velocity-exchange/sdk';
import {
	UIMarket,
	MarketId,
	getBaseAssetSymbol,
} from '@velocity-exchange/common';

import Env from 'src/environmentVariables/EnvironmentVariables';
import { DEFAULT_MARKET_PROPS, PageRoute } from 'src/constants/constants';
import { ReadonlyURLSearchParams } from 'next/navigation';
import { getIsZeroFeesActiveForMarket } from 'src/utils/zeroFees';

const SEVEN_DECIMALS = new BN(7);

export const getMarketFees = (
	user: User | undefined,
	marketType: MarketType,
	marketIndex: number,
	driftClient: VelocityClient | undefined
): {
	makerFeeMultiplier: number;
	makerFeePct: number;
	makerFeeBps: number;
	takerFeeMultiplier: number;
	takerFeePct: number;
	takerFeeBps: number;
} => {
	const canBeFetched =
		driftClient &&
		driftClient.isSubscribed &&
		driftClient.userStats &&
		driftClient.userStats.isSubscribed;

	const marketFees =
		canBeFetched && driftClient.getMarketFees(marketType, marketIndex, user);

	if (!canBeFetched || !marketFees) {
		return {
			makerFeeMultiplier: 0,
			makerFeePct: 0,
			makerFeeBps: -Env.defaultMakerRebateBps,
			takerFeeMultiplier: 0,
			takerFeePct: 0,
			takerFeeBps: Env.defaultTakerFeeBps,
		};
	}

	const makerFeeMultiplier = marketFees?.makerFee || 0;
	const makerFeeMultBigNum = BigNum.fromPrint(
		makerFeeMultiplier.toString(),
		SEVEN_DECIMALS
	);

	const makerFeePct = makerFeeMultBigNum.mul(new BN(100)).toNum();
	const makerFeeBps = makerFeeMultBigNum.mul(new BN(10000)).toNum();

	const takerFeeMultiplier = marketFees?.takerFee || 0;
	const takerFeeMultBigNum = BigNum.fromPrint(
		takerFeeMultiplier.toString(),
		SEVEN_DECIMALS
	);

	const takerFeePct = takerFeeMultBigNum.mul(new BN(100)).toNum();
	const takerFeeBps = takerFeeMultBigNum.mul(new BN(10000)).toNum();

	return {
		makerFeeMultiplier,
		makerFeePct,
		makerFeeBps,
		takerFeeMultiplier,
		takerFeePct,
		takerFeeBps,
	};
};

export const findMarketBySymbol = (
	symbol: string,
	allowSpotUsdc = true,
	solPerpDefault = false,
	poolId?: number
): UIMarket | undefined => {
	const solPerp = UIMarket.createPerpMarket(0);

	if (!symbol) {
		return solPerpDefault ? solPerp : undefined;
	}

	const lowerCaseSymbol = symbol.toLowerCase();
	let market: UIMarket | undefined = undefined;

	let configFound: PerpMarketConfig | SpotMarketConfig | undefined =
		UIMarket.perpMarkets.find(
			(mkt) => mkt.symbol.toLowerCase() === lowerCaseSymbol
		);

	if (!configFound) {
		const baseAssetSymbol = getBaseAssetSymbol(symbol);

		const findMarketWithSymbol = (searchSymbol: string) => {
			return UIMarket.spotMarkets.find(
				(mkt) =>
					mkt.symbol.toLowerCase() === searchSymbol.toLowerCase() &&
					(poolId === undefined || mkt.poolId === poolId)
			);
		};

		if (poolId !== undefined) {
			configFound = findMarketWithSymbol(symbol);

			if (!configFound) {
				configFound = findMarketWithSymbol(baseAssetSymbol);
			}

			if (!configFound) {
				const symbolWithPoolId = `${baseAssetSymbol}-${poolId}`;
				configFound = findMarketWithSymbol(symbolWithPoolId);
			}
		} else {
			configFound = findMarketWithSymbol(baseAssetSymbol);
		}

		if (configFound) {
			market = UIMarket.createSpotMarket(configFound.marketIndex);
		}
	} else {
		market = UIMarket.createPerpMarket(configFound.marketIndex);
	}

	if (!market) {
		return solPerpDefault ? solPerp : undefined;
	}

	if (market.isUsdcMarket && !allowSpotUsdc) {
		return solPerpDefault ? solPerp : undefined;
	}

	return market;
};

export const getSymbolFromFullMarketName = (marketName: string) => {
	if (marketName.includes('-PERP')) return marketName;
	return marketName.replace('/USDC', '');
};

export const getMarketPrecision = (marketId: MarketId) => {
	return marketId.isPerp
		? BASE_PRECISION_EXP
		: UIMarket.spotMarkets[marketId.marketIndex].precisionExp;
};

const MIN_HOT_THRESHOLD = 100_000;

export const marketIsHot = (
	marketZScore: number,
	medianZScore: number,
	volume24h: number
) => {
	return volume24h >= MIN_HOT_THRESHOLD && marketZScore - medianZScore > 2;
};

export const isMajorMarket = (marketId: MarketId) => {
	return marketId.isPerp && marketId.marketIndex < 3; // SOL, BTC, ETH
};

export const getCleanedMarketSymbol = (marketSymbol: string) => {
	return marketSymbol.replace(/-[0-9]+$/, '');
};

export const getMarketFromUrl = (
	pathname: string,
	searchParams?: URLSearchParams | ReadonlyURLSearchParams
) => {
	const firstSubPath = pathname.split('/')[1];
	let possibleMarketSymbol = firstSubPath;

	if (firstSubPath === PageRoute.bet) {
		possibleMarketSymbol = pathname.split('/')[2];
	}

	const query = possibleMarketSymbol ?? searchParams?.get('market');

	if (!query) {
		return DEFAULT_MARKET_PROPS;
	}

	const targetUiMarket = findMarketBySymbol(query, false, false);
	if (targetUiMarket) {
		return {
			marketIndex: targetUiMarket.market?.marketIndex,
			marketType: targetUiMarket.marketType,
		};
	}

	const fuzzyMatchMarket = findMarketBySymbol(
		query,
		false,
		getIsZeroFeesActiveForMarket({ marketIndex: 1, isPerp: true })
			? false
			: true
	);
	if (fuzzyMatchMarket) {
		return {
			marketIndex: fuzzyMatchMarket.market?.marketIndex,
			marketType: fuzzyMatchMarket.marketType,
		};
	}

	return DEFAULT_MARKET_PROPS;
};
